import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProperties } from '@/hooks/useProperties';
import { uniqueSorted } from '@/lib/uniqueSorted';

const staticFilters = {
  tipo: ['Todos os tipos', 'Casa', 'Apartamento', 'Terreno', 'Sala Comercial', 'Chácara'],
  finalidade: ['Venda e Locação', 'Venda', 'Locação'],
  valor: ['Valor mínimo - máximo', 'Até R$ 200.000', 'R$ 200.000 - R$ 500.000', 'R$ 500.000 - R$ 1.000.000', 'Acima de R$ 1.000.000'],
};

type FilterName = 'tipo' | 'finalidade' | 'cidade' | 'bairro' | 'valor';

const labels: Record<FilterName, string> = {
  tipo: 'Tipo de imóvel',
  finalidade: 'Finalidade',
  cidade: 'Cidade',
  bairro: 'Bairro',
  valor: 'Valor',
};

const order: FilterName[] = ['tipo', 'finalidade', 'cidade', 'bairro', 'valor'];

export default function SearchBar() {
  const navigate = useNavigate();
  const { properties } = useProperties();
  const [values, setValues] = useState<Record<FilterName, string>>({
    tipo: '', finalidade: '', cidade: '', bairro: '', valor: '',
  });

  const cidadeOptions = useMemo(
    () => ['Todas as cidades', ...uniqueSorted(properties.map((p) => p.city))],
    [properties]
  );
  const bairroOptions = useMemo(() => {
    const source = values.cidade ? properties.filter((p) => p.city === values.cidade) : properties;
    return ['Todos os bairros', ...uniqueSorted(source.map((p) => p.neighborhood))];
  }, [properties, values.cidade]);

  const dynamicOptions: Record<FilterName, string[]> = {
    ...staticFilters,
    cidade: cidadeOptions,
    bairro: bairroOptions,
  };

  const handleChange = (name: FilterName, value: string) => {
    setValues((current) => {
      const next = { ...current, [name]: value };
      if (name === 'cidade' && !bairroOptions.includes(next.bairro)) {
        next.bairro = '';
      }
      return next;
    });
  };

  const search = () => {
    const params = new URLSearchParams();
    Object.entries(values).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    navigate(`/imoveis${params.size ? `?${params.toString()}` : ''}`);
  };

  return (
    <section aria-label="Busca de imóveis" className="relative z-20 -mt-8 px-4 md:px-8">
      <div className="rounded-md border border-slate-200 bg-white p-4 shadow-xl md:p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[repeat(5,minmax(0,1fr))_auto]">
          {order.map((name) => (
            <label key={name} className="block border-r border-slate-200 px-2 last:border-r-0">
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                {labels[name]}
              </span>
              <span className="relative block">
                <select
                  value={values[name]}
                  onChange={(event) => handleChange(name, event.target.value)}
                  className="w-full appearance-none bg-white py-2 pr-7 text-xs text-slate-700 outline-none focus:text-slate-950"
                >
                  {dynamicOptions[name].map((option, index) => (
                    <option key={option} value={index === 0 ? '' : option}>{option}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-slate-700" />
              </span>
            </label>
          ))}

          <button
            type="button"
            onClick={search}
            className="flex min-h-12 items-center justify-center gap-2 self-end rounded bg-secondary-950 px-5 text-xs font-bold text-white transition-colors hover:bg-secondary-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
          >
            <i className="ri-search-line text-base" />
            BUSCAR IMÓVEIS
          </button>
        </div>
      </div>
    </section>
  );
}
